import "../style.scss";
import { IonItem } from "@ionic/react";
import { useRateNegotiationIsActive } from "@src/app/rateNegotiation/hooks/useRateNegotiationIsActive";
import { useDefinedAgent } from "@src/app/store/helperHooks";
import { useOpenShiftItem } from "@src/app/store/helperHooks/shiftStoreHelper";
import { setRecognizedHoliday } from "@src/app/store/session";
import { OpenShiftItemBookingStatus } from "@src/app/store/shift/model";
import { isShiftPriorityValid } from "@src/app/utils/shiftHelper";
import { useIsExtraTimePayEnabled } from "@src/appV2/Facilities/api/useIsExtraTimePayEnabled";
import { logEvent } from "@src/appV2/lib/analytics";
import { Facility } from "@src/lib/interface";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { ShiftItemAlerts } from "./ShiftItemAlerts";
import { ShiftItemView } from "./shiftItemView";
import {
  HCF_PROFILE_SOURCE,
  SEARCH_MODE,
  SHIFT_BOOKED_CONTEXT,
  USER_EVENTS,
} from "../../../constants";
import { useShiftItem } from "../custom-hooks/useShiftItemHook";
import { ShiftItemCategory, ShiftItemProps } from "../model";

export function ShiftItem(props: ShiftItemProps) {
  const {
    shift,
    isInstantBookingShift,
    updateShiftList,
    updateShiftFields,
    goToDocumentsPage,
    onFacilityDetailsClick,
    isHcpInstantPayEnabled,
    isDisabled,
    handleShiftActivityLog,
    shiftActivityLog,
    isLastMinuteShift,
    validator,
    className = "",
    pushNotification,
    showFacilityDetails = true,
    loggerSearchMode,
    onItemClick,
    isUrgentShift,
    searchMode,
    availableShiftIds,
    filterDistance,
    filterDays,
    hcfProfileSource,
    shiftSlots,
    setClickedInterestInShift,
    allowedAgentReq,
    shiftBookability,
    isSingleSourceOfBookabilityEnabled = false,
  } = props;

  const sourceContext: string | null = useMemo(() => {
    let context: string | null = null;

    if (hcfProfileSource) {
      if (hcfProfileSource === HCF_PROFILE_SOURCE.CALENDAR) {
        context = SHIFT_BOOKED_CONTEXT.HCF_CALENDAR;
      } else if (hcfProfileSource === HCF_PROFILE_SOURCE.MAP) {
        context = SHIFT_BOOKED_CONTEXT.HCF_MAP;
      }
    } else if (searchMode === SEARCH_MODE.TIME_SLOT) {
      context = SHIFT_BOOKED_CONTEXT.CALENDAR_DATE;
    } else if (searchMode === SEARCH_MODE.URGENT_SHIFTS) {
      context = SHIFT_BOOKED_CONTEXT.URGENT_SHIFTS;
    } else if (searchMode === SEARCH_MODE.NON_QFA_RANGE_SHIFTS) {
      context = SHIFT_BOOKED_CONTEXT.NON_QFA_RANGE_SHIFTS;
    }

    if (isLastMinuteShift) {
      context = SHIFT_BOOKED_CONTEXT.LAST_MINUTE_SHIFTS;
    } else if (isUrgentShift) {
      context = SHIFT_BOOKED_CONTEXT.URGENT_SHIFTS;
    } else if (searchMode) {
      context = SHIFT_BOOKED_CONTEXT[searchMode];
    }

    return context;
  }, [searchMode, hcfProfileSource, isLastMinuteShift, isUrgentShift]);

  const agent = useDefinedAgent();

  const openShiftItem = useOpenShiftItem(shift._id ?? "") ?? {};

  const isShiftInQfaRangeOrIsBookable =
    !openShiftItem?.types?.includes(SEARCH_MODE.NON_QFA_RANGE_SHIFTS) ||
    (openShiftItem?.types?.includes(SEARCH_MODE.NON_QFA_RANGE_SHIFTS) &&
      openShiftItem?.bookingStatus ===
        OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_PASS);

  const isRateNegotiationActive =
    useRateNegotiationIsActive(shift) &&
    isShiftInQfaRangeOrIsBookable &&
    (agent.isTestAccount ?? false) === (shift.facility?.isTestAccount ?? false) &&
    isInstantBookingShift &&
    !isUrgentShift &&
    !isLastMinuteShift;

  const {
    loadAlertDialog,
    category,
    alertOpen,
    onCancel,
    onConfirm,
    interested,
    pushNotificationOpen,
    closePushNotification,
    submitting,
    submitted,
    isLoading,
    workApproved,
    missingDocs,
    checkingDocs,
    onClosePrimerPage,
    changedShiftPayText,
    isMarkInterest,
  } = useShiftItem({
    shift,
    goToDocumentsPage,
    isInstantBookingShift,
    handleShiftActivityLog,
    updateShiftList,
    isLastMinuteShift: !!isLastMinuteShift,
    validator,
    pushNotification,
    shiftActivityLog,
    isUrgentShift,
    searchMode,
    availableShiftIds,
    filterDistance,
    filterDays,
    sourceContext,
    setClickedInterestInShift,
    isHcpInstantPayEnabled,
    isRateNegotiationActive,
  });

  const dispatch = useDispatch();

  const isExtraTimePayEnabled = useIsExtraTimePayEnabled(shift.facility);
  const etpEventOptions = {
    extra_time_pay_enabled: isExtraTimePayEnabled ? "yes" : "no",
    showing_extra_time_pay_icon: "yes",
  };

  useEffect(() => {
    if (isInstantBookingShift) {
      logEvent(USER_EVENTS.DISCOVERED_AN_INSTANT_BOOK_SHIFT);
    }
  }, [isInstantBookingShift]);

  useEffect(() => {
    if (alertOpen && !isLastMinuteShift) {
      if (category === ShiftItemCategory.BOOKING_WARNING) {
        if (isInstantBookingShift) {
          logEvent(USER_EVENTS.TAPPED_INSTANTBOOK_SHIFT, {
            search_mode: loggerSearchMode ? loggerSearchMode : "date",
            instant_book: true,
            instant_pay: shift?.isInstantPay,
            last_minute: isLastMinuteShift,
            shift_id: shift._id,
            ...etpEventOptions,
          });
        } else {
          logEvent(USER_EVENTS.TAPPED_BOOK_SHIFT, {
            search_mode: loggerSearchMode ? loggerSearchMode : "date",
            instant_pay: shift?.isInstantPay,
            last_minute: isLastMinuteShift,
            shift_id: shift._id,
            ...etpEventOptions,
          });
        }
      } else if (category === ShiftItemCategory.MISSING_CBH_DOCUMENTS) {
        logEvent(USER_EVENTS.VIEWED_CBH_MISSING_DOCS_POPUP);
      } else if (category === ShiftItemCategory.WELCOME_TO_INSTANTPAY) {
        logEvent(USER_EVENTS.VIEWED_WELCOME_TO_INSTANTPAY);
      }
    }

    // FIXME: Disabling it temporarily to avoid regression on this item, it should be looked upon as part of refactoring of the Shift Item card
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertOpen, category, isInstantBookingShift, isLastMinuteShift]);

  useEffect(() => {
    if (shift.isHolidayShift) {
      dispatch(setRecognizedHoliday(shift.holidayName ?? ""));
    }
  }, [dispatch, shift.holidayName, shift.isHolidayShift]);

  const isShiftInstantPayAllowed = shift.isInstantPay ?? !shift.isHCFInstantPayProhibited;
  const isInstantPayAllowed = isShiftInstantPayAllowed && isHcpInstantPayEnabled;

  const onClickOnFacilityDetails = (displayExtraTimePayCard = false) => {
    const facility = shift?.facility as Facility;
    /**
     * Note - this is directly mutating an object in redux store, which could
     * cause bugs.
     */
    facility.isInstantPayEnabled = !shift.isHCFInstantPayProhibited;
    onFacilityDetailsClick?.(shift, isInstantBookingShift as boolean, displayExtraTimePayCard);
  };

  const [priorityShift, setPriorityShift] = useState(
    shift?.priorityTill ? isShiftPriorityValid(shift.priorityTill) : false
  );
  const shiftSlot = shiftSlots?.find((shiftSlot) => {
    if (
      shift.start === shiftSlot.start.toString() &&
      shift.end === shiftSlot.end.toString() &&
      shift?.facilityId?.toString() === shiftSlot.workplaceUserId
    ) {
      return true;
    }
    return false;
  });

  const getInstantBookShiftStatus = () => {
    if (
      searchMode === SEARCH_MODE.NON_QFA_RANGE_SHIFTS ||
      searchMode === SEARCH_MODE.WORKER_AVAILABILITY
    ) {
      return !isMarkInterest;
    }
    return isInstantBookingShift;
  };

  return (
    <IonItem
      className={`${
        !workApproved && priorityShift === true
          ? "shift-item__container-priority-shift"
          : "shift-item__container"
      } ${className}`}
      data-testid={`shift-item-container-${shift.shiftId}`}
      disabled={isDisabled}
    >
      <ShiftItemView
        isLoading={isLoading}
        submitted={submitted}
        submitting={submitting}
        loadAlertDialog={loadAlertDialog}
        onClickOnFacilityDetails={onClickOnFacilityDetails}
        shiftDetails={shift}
        isInstantPayAllowed={isInstantPayAllowed as boolean}
        isInstantBookingShift={isInstantBookingShift}
        category={category}
        interested={interested}
        workApproved={workApproved}
        isLastMinuteShift={isLastMinuteShift}
        showFacilityDetails={showFacilityDetails}
        onItemClick={onItemClick}
        isUrgentShift={isUrgentShift}
        checkingDocs={checkingDocs}
        priorityShift={priorityShift}
        setPriorityShift={setPriorityShift}
        shiftSlot={shiftSlot}
        isRateNegotiationActive={isRateNegotiationActive}
        updateShiftFields={updateShiftFields}
        allowedAgentReq={allowedAgentReq}
        searchMode={searchMode}
        shiftBookability={shiftBookability}
        isSingleSourceOfBookabilityEnabled={isSingleSourceOfBookabilityEnabled}
        updateShiftList={updateShiftList}
      />
      <ShiftItemAlerts
        shift={shift}
        category={category}
        isInstantBookingShift={getInstantBookShiftStatus()}
        alertOpen={alertOpen}
        onConfirm={onConfirm}
        onCancel={onCancel}
        pushNotificationOpen={pushNotificationOpen}
        closePushNotification={closePushNotification}
        isUrgentShift={isUrgentShift}
        missingDocs={missingDocs}
        onClosePrimerPage={onClosePrimerPage}
        changedShiftPayText={changedShiftPayText}
      />
    </IonItem>
  );
}
